import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import MatchStatusIndicator from '../../components/ui/MatchStatusIndicator';
import UserContextSwitcher from '../../components/ui/UserContextSwitcher';

// Import dashboard components
import UpcomingMatchCard from './components/UpcomingMatchCard';
import AchievementPanel from './components/AchievementPanel';
import QuickMatchCreator from './components/QuickMatchCreator';
import ActivityFeed from './components/ActivityFeed';
import SuggestedMatches from './components/SuggestedMatches';
import WeatherWidget from './components/WeatherWidget';

const Dashboard = () => {
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [recentBadges, setRecentBadges] = useState([]);
  const [activities, setActivities] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Mock data initialization
  useEffect(() => {
    const initializeDashboard = () => {
      // Mock upcoming matches
      const mockMatches = [
      {
        id: 1,
        title: 'Jogo de Sábado',
        venue: 'Campo do Morumbi',
        date: '2025-10-18',
        time: '14:00',
        status: 'confirmed',
        userStatus: 'confirmed',
        attendees: {
          confirmed: 8,
          pending: 4,
          total: 12
        },
        payment: {
          amount: 25.00,
          paid: 6,
          pending: 6,
          total: 12
        },
        weather: {
          temperature: 24,
          condition: 'Parcialmente nublado',
          icon: 'Cloud',
          rainChance: 20
        },
        confirmedPlayers: [
        {
          name: 'João Silva',
          avatar: "https://images.unsplash.com/photo-1641479160067-5ae7bde244b0",
          avatarAlt: 'Professional headshot of young man with short brown hair in casual shirt'
        },
        {
          name: 'Pedro Santos',
          avatar: "https://images.unsplash.com/photo-1667575949231-fbf430640797",
          avatarAlt: 'Professional headshot of man with beard in blue shirt'
        },
        {
          name: 'Carlos Lima',
          avatar: "https://images.unsplash.com/photo-1723607528434-21cde67167c4",
          avatarAlt: 'Professional headshot of man with glasses in white shirt'
        }]

      },
      {
        id: 2,
        title: 'Racha de Domingo',
        venue: 'Quadra da Vila',
        date: '2025-10-19',
        time: '16:00',
        status: 'pending',
        userStatus: 'pending',
        attendees: {
          confirmed: 5,
          pending: 7,
          total: 12
        },
        payment: {
          amount: 20.00,
          paid: 3,
          pending: 9,
          total: 12
        },
        weather: {
          temperature: 26,
          condition: 'Ensolarado',
          icon: 'Sun',
          rainChance: 5
        },
        confirmedPlayers: [
        {
          name: 'Ana Costa',
          avatar: "https://images.unsplash.com/photo-1628013663808-25ef6a3b28b4",
          avatarAlt: 'Professional headshot of woman with long brown hair in business attire'
        },
        {
          name: 'Rafael Oliveira',
          avatar: "https://images.unsplash.com/photo-1706823906110-40052599e7d3",
          avatarAlt: 'Professional headshot of young man with curly hair in casual wear'
        }]

      }];


      // Mock user statistics
      const mockStats = {
        week: {
          matches: 2,
          wins: 1,
          goals: 3,
          reputation: 4.2
        },
        month: {
          matches: 8,
          wins: 5,
          goals: 12,
          reputation: 4.5
        },
        year: {
          matches: 45,
          wins: 28,
          goals: 67,
          reputation: 4.3
        }
      };

      // Mock recent badges
      const mockBadges = [
      {
        id: 1,
        title: 'Pontualidade',
        description: 'Chegou no horário em 5 jogos seguidas',
        type: 'attendance',
        earnedAt: '2 dias atrás'
      },
      {
        id: 2,
        title: 'Organizador',
        description: 'Criou 10 jogos este mês',
        type: 'organizer',
        earnedAt: '1 semana atrás'
      },
      {
        id: 3,
        title: 'Pagador em Dia',
        description: 'Pagou todos os jogos antecipadamente',
        type: 'payment',
        earnedAt: '2 semanas atrás'
      }];


      // Mock activities
      const mockActivities = [
      {
        id: 1,
        type: 'match_created',
        title: 'Novo jogo criado',
        description: 'Você criou "Jogo de Sábado" no Campo do Morumbi',
        timestamp: new Date(Date.now() - 3600000),
        matchId: 1,
        metadata: {
          venue: 'Campo do Morumbi',
          players: 12,
          amount: 25.00
        }
      },
      {
        id: 2,
        type: 'match_joined',
        title: 'Confirmação de presença',
        description: 'Pedro Santos confirmou presença no seu jogo',
        timestamp: new Date(Date.now() - 7200000),
        matchId: 1,
        participants: [
        {
          name: 'Pedro Santos',
          avatar: "https://images.unsplash.com/photo-1667575949231-fbf430640797",
          avatarAlt: 'Professional headshot of man with beard in blue shirt'
        }]

      },
      {
        id: 3,
        type: 'barbecue_planned',
        title: 'Churrasco organizado',
        description: 'Churrasco pós-jogo foi planejado para domingo',
        timestamp: new Date(Date.now() - 10800000),
        eventId: 1,
        participants: [
        {
          name: 'João Silva',
          avatar: "https://images.unsplash.com/photo-1641479160067-5ae7bde244b0",
          avatarAlt: 'Professional headshot of young man with short brown hair in casual shirt'
        },
        {
          name: 'Carlos Lima',
          avatar: "https://images.unsplash.com/photo-1723607528434-21cde67167c4",
          avatarAlt: 'Professional headshot of man with glasses in white shirt'
        }]

      },
      {
        id: 4,
        type: 'achievement',
        title: 'Novo badge conquistado',
        description: 'Você ganhou o badge "Pontualidade"',
        timestamp: new Date(Date.now() - 172800000)
      }];


      // Mock suggestions
      const mockSuggestions = [
      {
        id: 3,
        title: 'Jogo da Galera',
        venue: 'Arena Central',
        date: '2025-10-20',
        time: '15:00',
        pricePerPlayer: 30.00,
        confirmedPlayers: 7,
        maxPlayers: 12,
        distance: 2.3,
        reasons: [
        { type: 'location', text: 'Perto de você' },
        { type: 'friends', text: '3 amigos participando' },
        { type: 'skill', text: 'Nível similar' }],

        mutualFriends: [
        {
          name: 'Lucas Ferreira',
          avatar: "https://images.unsplash.com/photo-1663720527180-4c60a78fe3b7",
          avatarAlt: 'Professional headshot of man with short dark hair in suit'
        },
        {
          name: 'Marcos Alves',
          avatar: "https://images.unsplash.com/photo-1663720527180-4c60a78fe3b7",
          avatarAlt: 'Professional headshot of man with styled hair in casual shirt'
        },
        {
          name: 'Felipe Costa',
          avatar: "https://images.unsplash.com/photo-1706823906110-40052599e7d3",
          avatarAlt: 'Professional headshot of young man with curly hair in casual wear'
        }]

      },
      {
        id: 4,
        title: 'Futebol de Terça',
        venue: 'Campo dos Jardins',
        date: '2025-10-21',
        time: '19:00',
        pricePerPlayer: 22.00,
        confirmedPlayers: 9,
        maxPlayers: 14,
        distance: 1.8,
        reasons: [
        { type: 'time', text: 'Horário preferido' },
        { type: 'venue', text: 'Local favorito' }],

        mutualFriends: [
        {
          name: 'Roberto Silva',
          avatar: "https://images.unsplash.com/photo-1723607528434-21cde67167c4",
          avatarAlt: 'Professional headshot of man with glasses in white shirt'
        }]

      }];


      setUpcomingMatches(mockMatches);
      setUserStats(mockStats);
      setRecentBadges(mockBadges);
      setActivities(mockActivities);
      setSuggestions(mockSuggestions);
      setIsLoading(false);
    };

    // Simulate loading delay
    const timer = setTimeout(initializeDashboard, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleAttendanceChange = (matchId, status) => {
    setUpcomingMatches((prev) => prev?.map((match) =>
    match?.id === matchId ?
    { ...match, userStatus: status } :
    match
    ));
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'create-match':navigate('/match-details');
        break;
      case 'find-venue':navigate('/venue-search');
        break;
      case 'plan-barbecue':navigate('/barbecue-planning');
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-64 bg-muted rounded-lg"></div>
                <div className="h-48 bg-muted rounded-lg"></div>
              </div>
              <div className="space-y-6">
                <div className="h-80 bg-muted rounded-lg"></div>
                <div className="h-64 bg-muted rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6">
        {/* Welcome Section */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Bem-vindo de volta! ⚽
            </h1>
            <p className="text-muted-foreground">
              Gerencie seus jogos, acompanhe conquistas e organize churrascos
            </p>
          </div>
          
          <div className="hidden lg:flex items-center space-x-4">
            <MatchStatusIndicator />
            <UserContextSwitcher />
          </div>
        </div>

        {/* Quick Actions Bar - Mobile */}
        <div className="lg:hidden mb-6">
          <div className="flex space-x-3 overflow-x-auto pb-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => handleQuickAction('create-match')}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
              className="whitespace-nowrap">

              Nova Jogo
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickAction('find-venue')}
              iconName="MapPin"
              iconPosition="left"
              iconSize={16}
              className="whitespace-nowrap">

              Buscar Quadra
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleQuickAction('plan-barbecue')}
              iconName="Utensils"
              iconPosition="left"
              iconSize={16}
              className="whitespace-nowrap">

              Churrasco
            </Button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Matches */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-heading font-bold text-foreground">
                  Próximos Jogos
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/match-details')}
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={16}>

                  Novo Jogo
                </Button>
              </div>
              
              {upcomingMatches?.length > 0 ?
              <div className="space-y-4">
                  {upcomingMatches?.map((match) =>
                <UpcomingMatchCard
                  key={match?.id}
                  match={match}
                  onAttendanceChange={handleAttendanceChange} />

                )}
                </div> :

              <div className="bg-card border border-border rounded-lg p-8 text-center">
                  <div className="flex items-center justify-center w-16 h-16 bg-muted/50 rounded-lg mx-auto mb-4">
                    <Icon name="Calendar" size={24} className="text-muted-foreground" />
                  </div>
                  <h3 className="font-heading font-semibold text-card-foreground mb-2">
                    Nenhum jogo agendado
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Crie seu primeiro jogo ou participe de um existente
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                    variant="default"
                    onClick={() => navigate('/match-details')}
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={16}>

                      Criar Jogo
                    </Button>
                    <Button
                    variant="outline"
                    onClick={() => navigate('/venue-search')}
                    iconName="Search"
                    iconPosition="left"
                    iconSize={16}>

                      Buscar Jogos
                    </Button>
                  </div>
                </div>
              }
            </section>

            {/* Quick Match Creator */}
            <QuickMatchCreator />

            {/* Activity Feed */}
            <ActivityFeed activities={activities} />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Weather Widget */}
            <WeatherWidget location="São Paulo, SP" />

            {/* Achievement Panel */}
            <AchievementPanel
              userStats={userStats}
              recentBadges={recentBadges} />


            {/* Suggested Matches */}
            <SuggestedMatches suggestions={suggestions} />

            {/* Quick Stats Card */}
            <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
              <h3 className="font-heading font-semibold text-card-foreground mb-3">
                Estatísticas Rápidas
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Este mês</span>
                  </div>
                  <span className="text-sm font-medium text-card-foreground">
                    {userStats?.month?.matches || 0} jogos
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Trophy" size={16} className="text-success" />
                    <span className="text-sm text-muted-foreground">Taxa de vitórias</span>
                  </div>
                  <span className="text-sm font-medium text-card-foreground">
                    {userStats?.month ? Math.round(userStats?.month?.wins / userStats?.month?.matches * 100) : 0}%
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Star" size={16} className="text-warning" />
                    <span className="text-sm text-muted-foreground">Reputação</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-sm font-medium text-card-foreground">
                      {userStats?.month?.reputation?.toFixed(1) || '0.0'}
                    </span>
                    <Icon name="Star" size={12} className="text-warning" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Quick Action Button - Mobile */}
      <QuickActionButton />
    </div>);

};

export default Dashboard;